import { useState } from 'react'
import { Plus, Minus, RotateCcw } from 'lucide-react'
import { useDragScroll } from '@/gf/hooks/useDragScroll'

interface ImageViewerProps {
  src: string
  alt?: string
  className?: string
}

const ImageViewer = ({ src, alt = 'Image preview', className = '' }: ImageViewerProps) => {
  const [scale, setScale] = useState(1)
  const dragScrollRef = useDragScroll<HTMLDivElement>()

  const zoomIn = (e: React.MouseEvent) => {
    e.preventDefault()
    setScale((prev) => Math.min(prev + 0.25, 3))
  }

  const zoomOut = (e: React.MouseEvent) => {
    e.preventDefault()
    setScale((prev) => Math.max(prev - 0.25, 0.5))
  }

  const reset = (e: React.MouseEvent) => {
    e.preventDefault()
    setScale(1)
  }

  return (
    <div className={`relative ${className}`}>
      <div className="absolute top-2 right-2 z-10 flex gap-2 bg-white/90 rounded-md p-1 shadow-sm">
        <button
          type="button"
          onClick={zoomIn}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          title="Zoom in"
        >
          <Plus className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={zoomOut}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          title="Zoom out"
        >
          <Minus className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={reset}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          title="Reset zoom"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={dragScrollRef}
        className="w-full h-[600px] border border-gray-200 rounded-md bg-gray-50 overflow-auto"
        style={{ cursor: scale > 1 ? 'grab' : 'default' }}
      >
        <div
          className={`${scale <= 1 ? 'h-full flex items-center justify-center' : ''}`}
          style={{
            width: scale > 1 ? `${100 * scale}%` : '100%',
            height: scale > 1 ? `${100 * scale}%` : '100%',
            minWidth: scale > 1 ? `${600 * scale}px` : 'auto',
            minHeight: scale > 1 ? `${400 * scale}px` : 'auto',
          }}
        >
          <img
            src={src}
            alt={alt}
            className="transition-transform duration-200 object-contain"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'center center',
              maxWidth: scale <= 1 ? '100%' : 'none',
              maxHeight: scale <= 1 ? '100%' : 'none',
              width: scale > 1 ? '100%' : 'auto',
              height: scale > 1 ? '100%' : 'auto',
            }}
            draggable={false}
          />
        </div>
      </div>
    </div>
  )
}

export default ImageViewer
